/**
 * Both endpoints answer with the same `AssetWorkflowResponseDto` as the read, so the
 * mutation hooks seed the read cache from the response instead of refetching.
 *
 * Steps are named by id rather than position: on an any-order chain position carries no
 * ordering meaning, and the backend cannot infer a target when several steps are actionable.
 *
 * Each call suppresses the global error toast so callers own the error UX via
 * `parseProblemDetail` + `notificationService`.
 */
import {
  AssetApprovalActionsService,
  type AssetWorkflowResponseDto,
} from '@inference.engine/workspace-api-client/web';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

export const approveAssetStep = async (
  projectId: string,
  assetId: string,
  stepId?: string,
): Promise<AssetWorkflowResponseDto> => {
  const response = await AssetApprovalActionsService.approve({
    client: workspaceHttp,
    path: { platformProjectId: projectId, assetId },
    body: stepId === undefined ? {} : { stepId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to approve step');
  }

  return response.data;
};

export const unapproveAssetStep = async (
  projectId: string,
  assetId: string,
  stepId?: string,
): Promise<AssetWorkflowResponseDto> => {
  const response = await AssetApprovalActionsService.unapprove({
    client: workspaceHttp,
    path: { platformProjectId: projectId, assetId },
    body: stepId === undefined ? {} : { stepId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to unapprove step');
  }

  return response.data;
};

export const overrideAssetStep = async (
  projectId: string,
  assetId: string,
  stepId: string,
): Promise<AssetWorkflowResponseDto> => {
  const response = await AssetApprovalActionsService.overrideStep({
    client: workspaceHttp,
    path: { platformProjectId: projectId, assetId },
    body: { stepId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to override step');
  }

  return response.data;
};

export const undoAssetStepOverride = async (
  projectId: string,
  assetId: string,
  stepId: string,
): Promise<AssetWorkflowResponseDto> => {
  const response = await AssetApprovalActionsService.undoStepOverride({
    client: workspaceHttp,
    path: { platformProjectId: projectId, assetId },
    body: { stepId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to undo step override');
  }

  return response.data;
};
